import { TabType } from '@engbers/components/header-main';
import { EngbersOsBottomBarItemId } from '@engbers/components/online-shops/en-os-bottombar';

type FormatMessageFn = (args: { id: string; values?: Record<string, any> }) => string;
type CountType = 'cart' | 'wishlist';

const TYPE_CONFIG: Record<
  CountType,
  {
    singularLabel: string;
    pluralLabel: string;
    containerLabel: string;
  }
> = {
  cart: {
    singularLabel: 'ariaLabel.cart.amount.singular',
    pluralLabel: 'ariaLabel.cart.amount.plural',
    containerLabel: 'ariaLabel.cart.amount.container',
  },
  wishlist: {
    singularLabel: 'ariaLabel.wishlist.amount.singular',
    pluralLabel: 'ariaLabel.wishlist.amount.plural',
    containerLabel: 'ariaLabel.wishlist.amount.container',
  },
};

const TYPE_ALIASES: Record<TabType | EngbersOsBottomBarItemId, CountType | null> = {
  cart: 'cart',
  isBasket: 'cart',
  wishlist: 'wishlist',
  isWishlist: 'wishlist',
  homePage: null,
  menu: null,
  isMyAccount: null,
  account: null,
};

export const getLabel = (
  formatMessage: FormatMessageFn,
  type: TabType | EngbersOsBottomBarItemId,
  count?: number,
  isModalActive?: boolean,
): string | null => {
  const resolved = TYPE_ALIASES[type];

  if (resolved && typeof count === 'number') {
    if (count === 1) {
      return formatMessage({ id: TYPE_CONFIG[resolved].singularLabel });
    }

    const displayCount = count >= 100 ? '+99' : count.toString();
    return formatMessage({
      id: TYPE_CONFIG[resolved].pluralLabel,
      values: { count: displayCount },
    });
  }

  switch (type) {
    case 'homePage':
      return formatMessage({ id: 'ariaLabel.navigation.home' });
    case 'menu':
      return formatMessage({
        id: isModalActive ? 'ariaLabel.navigation.menu.close' : 'ariaLabel.navigation.menu.show',
      });
    case 'isMyAccount':
    case 'account':
      return formatMessage({ id: 'ariaLabel.navigation.account' });
    default:
      return null;
  }
};

export const getExtendedCountLabel = (
  formatMessage: FormatMessageFn,
  type: TabType | EngbersOsBottomBarItemId,
  label: string | null,
): string | null => {
  const resolved = TYPE_ALIASES[type];

  if (!label || !resolved) {
    return label;
  }

  return formatMessage({
    id: TYPE_CONFIG[resolved].containerLabel,
    values: { label },
  });
};

export const getCountForItem = (
  type: TabType | EngbersOsBottomBarItemId,
  wishlistCount: number,
  lineItemsCount: number,
): number | null => {
  const resolved = TYPE_ALIASES[type];

  switch (resolved) {
    case 'cart':
      return lineItemsCount;
    case 'wishlist':
      return wishlistCount;
    default:
      return null;
  }
};

export const shouldShowCountBadge = (type: TabType | EngbersOsBottomBarItemId): boolean => {
  return !!TYPE_ALIASES[type];
};
